/**
 * InfoRow - Read-only label/value pair molecule.
 *
 * Atomic Design role: Molecule. Renders a single <dt>/<dd> pair using
 * the project's small-uppercase-label + 14px-value pattern. Used by
 * InfoSection and the WO/Inspection detail pages to surface read-only
 * fields in a consistent layout.
 *
 * Use the `value` prop for plain string or numeric values; pass
 * `children` instead when the value is a JSX node (e.g. a badge, link,
 * or multi-line block). When `value` is empty/null/undefined the row
 * renders the configurable `emptyText` (default em-dash) so empty
 * values don't visually disappear.
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface InfoRowProps {
	/** Uppercase label (the <dt>). */
	label: string;
	/** Plain text or numeric value (the <dd>). Ignored when children is set. */
	value?: string | number | null;
	/** JSX children rendered inside <dd>. Takes precedence over value. */
	children?: React.ReactNode;
	/** Placeholder rendered when value is empty/null/undefined. Default: '—'. */
	emptyText?: string;
	/** Whether to preserve newlines in the value (for free-text fields). */
	preserveNewlines?: boolean;
	/** Optional className appended to the <dd> wrapper. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const InfoRow: React.FC<InfoRowProps> = ({
	label,
	value,
	children,
	emptyText = '—',
	preserveNewlines = false,
	className,
}) => {
	/* Resolve what to render in the <dd>. JSX children win; otherwise
	   fall back to the value, with emptyText as the placeholder when
	   the value is missing or empty-string. */
	const hasValue = children !== undefined
		|| (value !== undefined && value !== null && String(value).trim() !== '');

	return (
		<div className="info-row" data-ref="molecule-info-row">
			<dt className="info-row__label text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wider">{label}</dt>
			<dd className={`info-row__value text-14px font-sans mt-2px${preserveNewlines ? ' whitespace-pre-line' : ''}${className ? ` ${className}` : ''}`}>
				{hasValue ? (children ?? value) : <span className="text-brand-text-muted">{emptyText}</span>}
			</dd>
		</div>
	);
};

export default InfoRow;
